import * as React from 'react';
import {
  AddTile,
  ConfirmDialog,
  NoteCard,
  NoteComposer,
  Stack,
  Text,
} from '@relay-towing/ui';
import type { Adder } from './adder';

export interface SectionNote {
  id: string;
  text: string;
  /** Who wrote it; the server always sends the writer's name. */
  author: string;
  createdAt: string;
  /** The viewer wrote it, so they may change or delete it. */
  own: boolean;
}

const MINUTE = 60 * 1000;

/** "Just now", "10 min ago", then the date, as the Figma's notes read. */
const when = (iso: string, now = Date.now()) => {
  const ago = now - new Date(iso).getTime();
  if (ago < MINUTE) return 'Just now';
  if (ago < 60 * MINUTE) return `${Math.floor(ago / MINUTE)} min ago`;
  return new Date(iso).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export interface NotesSectionProps {
  notes: SectionNote[];
  /** May add notes; the server refuses anyone else. */
  editable: boolean;
  saving: boolean;
  onAdd: (text: string) => Promise<unknown>;
  onChange: (id: string, text: string) => Promise<unknown>;
  onDelete: (id: string) => Promise<unknown>;
  deleting: boolean;
}

/**
 * Notes, newest first, with the Add note tile under them, as the Figma's
 * Add note frames draw them: a note just added takes the top. The viewer's
 * own notes say "You" and open to change or delete; the server refuses
 * anyone else's, so theirs do not open. It draws what it is given, so the
 * screen decides whose notes they are: a call's and an impound's are both
 * their vehicle's.
 */
export const NotesSection = React.forwardRef<Adder, NotesSectionProps>(
  function NotesSection(
    { notes, editable, saving, onAdd, onChange, onDelete, deleting },
    ref
  ) {
    const [writing, setWriting] = React.useState(false);
    React.useImperativeHandle(ref, () => ({ add: () => setWriting(true) }), []);
    const [changing, setChanging] = React.useState<SectionNote | null>(null);
    const [confirming, setConfirming] = React.useState(false);
    const newestFirst = [...notes].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    return (
      <Stack gap={4}>
        <Text variant="h2" as="h2">
          Notes
        </Text>
        {newestFirst.map((note) => (
          <NoteCard
            key={note.id}
            appearance="outline"
            author={note.own ? 'You' : note.author}
            time={when(note.createdAt)}
            onOpen={note.own ? () => setChanging(note) : undefined}
          >
            {note.text}
          </NoteCard>
        ))}
        {editable ? (
          <AddTile width="block" onClick={() => setWriting(true)}>
            Add note
          </AddTile>
        ) : notes.length === 0 ? (
          <Text variant="body" tone="muted">
            No notes yet.
          </Text>
        ) : null}
        <NoteComposer
          open={writing}
          onOpenChange={setWriting}
          saving={saving}
          onSubmit={async (text) => {
            await onAdd(text);
            setWriting(false);
          }}
        />
        <NoteComposer
          open={changing !== null}
          onOpenChange={(open) => !open && setChanging(null)}
          title="Edit note"
          defaultText={changing?.text}
          saving={saving}
          onSubmit={async (text) => {
            if (!changing) return;
            await onChange(changing.id, text);
            setChanging(null);
          }}
          onDelete={() => setConfirming(true)}
        />
        <ConfirmDialog
          open={confirming}
          onOpenChange={setConfirming}
          title="Delete this note?"
          description="It goes from the call for everyone. This cannot be undone."
          confirmLabel="Delete note"
          destructive
          loading={deleting}
          onConfirm={() => {
            if (!changing) return;
            // A failed delete leaves the note open, to try again or keep.
            void onDelete(changing.id)
              .then(
                () => setChanging(null),
                () => undefined
              )
              .finally(() => setConfirming(false));
          }}
        />
      </Stack>
    );
  }
);
